/**
 * Capacitor biometric login adapter (Phase 1 of biometric-login feature).
 *
 * Architecture: pairs @aparajita/capacitor-biometric-auth (biometric prompt)
 * with @aparajita/capacitor-secure-storage (keychain storage). The biometric
 * prompt acts as a UX gate before reading the stored JWT from keychain.
 *
 * Security note: the keychain entry uses standard whenUnlocked access, not
 * a biometric ACL (kSecAttrAccessControl with .biometryCurrentSet). Code with
 * execution inside this app's sandbox could read the JWT without invoking
 * the biometric prompt. This is acceptable for our threat model — local
 * attackers with code execution in the app process could steal the user's
 * password through the same channel. The biometric prompt protects against
 * the realistic attack vector: someone picking up an unlocked device and
 * opening the app. For OS-enforced biometric ACL we would need a custom
 * native plugin; capacitor-native-biometric is Capacitor 3/4 only and not
 * an option for Capacitor 8.
 *
 * `clearStoredCredentialOnly()` is a Capacitor-specific affordance (not on
 * the BiometricAdapter contract) that wipes the keychain entry but leaves
 * the `biometric_enrolled` flag intact — used by the cold-launch path when
 * a stored JWT is rejected by the server, so the next successful password
 * login can re-enroll automatically.
 */

import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';
import { SecureStorage, KeychainAccess } from '@aparajita/capacitor-secure-storage';
import { biometricStorage } from './biometricStorage';
import { BiometricError, BiometricErrorCode } from './BiometricAdapter';

const REASON = 'Sign in to Live Catalog';
const STORAGE_KEY = 'biometric_jwt';

// BiometryErrorType string values from @aparajita/capacitor-biometric-auth.
// Hard-coded because webpack can't resolve the named exports from the
// package's `export *` chain (its package.json points main at a file that
// doesn't exist; only BiometricAuth gets through the ESM import). Values are
// stable across plugin versions — last verified v9.1.2.
const ERR_USER_CANCEL = 'userCancel';
const ERR_APP_CANCEL = 'appCancel';
const ERR_SYSTEM_CANCEL = 'systemCancel';
const ERR_USER_FALLBACK = 'userFallback';
const ERR_BIOMETRY_NOT_AVAILABLE = 'biometryNotAvailable';
const ERR_BIOMETRY_NOT_ENROLLED = 'biometryNotEnrolled';
const ERR_PASSCODE_NOT_SET = 'passcodeNotSet';
const ERR_BIOMETRY_LOCKOUT = 'biometryLockout';

function mapPluginError(err) {
	const code = err?.code;
	if (typeof code !== 'string') {
		return new BiometricError(BiometricErrorCode.UNKNOWN, err?.message, err);
	}
	switch (code) {
		case ERR_USER_CANCEL:
		case ERR_APP_CANCEL:
		case ERR_SYSTEM_CANCEL:
		case ERR_USER_FALLBACK:
			return new BiometricError(BiometricErrorCode.USER_CANCEL, err.message, err);
		case ERR_BIOMETRY_NOT_AVAILABLE:
		case ERR_BIOMETRY_NOT_ENROLLED:
		case ERR_PASSCODE_NOT_SET:
			return new BiometricError(BiometricErrorCode.UNAVAILABLE, err.message, err);
		case ERR_BIOMETRY_LOCKOUT:
			return new BiometricError(BiometricErrorCode.LOCKOUT, err.message, err);
		default:
			return new BiometricError(BiometricErrorCode.UNKNOWN, err.message, err);
	}
}

export const CapacitorBiometric = {
	async isAvailable() {
		try {
			const result = await BiometricAuth.checkBiometry();
			return !!result.isAvailable;
		} catch {
			return false;
		}
	},

	async isEnrolled() {
		return biometricStorage.isEnrolled();
	},

	async enroll(jwt) {
		try {
			await SecureStorage.set(STORAGE_KEY, jwt, false, false, KeychainAccess.whenUnlocked);
		} catch (err) {
			throw new BiometricError(BiometricErrorCode.UNKNOWN, err?.message, err);
		}
		await biometricStorage.setEnrolled(true);
	},

	async authenticate() {
		try {
			await BiometricAuth.authenticate({
				reason: REASON,
				allowDeviceCredential: false,
				androidBiometryStrength: 'strong',
			});
		} catch (err) {
			throw mapPluginError(err);
		}
		let jwt;
		try {
			jwt = await SecureStorage.get(STORAGE_KEY);
		} catch (err) {
			throw new BiometricError(BiometricErrorCode.UNKNOWN, err?.message, err);
		}
		if (jwt == null) {
			throw new BiometricError(BiometricErrorCode.INVALID, 'no stored credential');
		}
		return jwt;
	},

	async disable() {
		try {
			await SecureStorage.remove(STORAGE_KEY);
		} catch {
			// idempotent — proceed to clear flag even if remove fails
		}
		await biometricStorage.setEnrolled(false);
	},

	/**
	 * Removes ONLY the keychain entry. Does NOT touch the `biometric_enrolled`
	 * flag. Used when a stored JWT is rejected by the server (cold-launch
	 * path) so the user's enrollment intent is preserved and the next
	 * successful password login re-enrolls automatically.
	 */
	async clearStoredCredentialOnly() {
		try {
			await SecureStorage.remove(STORAGE_KEY);
		} catch {
			// idempotent
		}
	},
};
